/**
 * Platform-specific token storage abstraction
 * - Mobile (React Native): Uses MMKV for fast, synchronous, encrypted storage
 * - Web: Uses localStorage with JSON serialization
 */

import { Platform } from 'react-native';

// MMKV types (will be imported conditionally on mobile)
interface MMKV {
  set(key: string, value: string): void;
  getString(key: string): string | undefined;
  delete(key: string): void;
  clearAll(): void;
}

// Token data structure
export interface TokenData {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth.accessToken',
  REFRESH_TOKEN: 'auth.refreshToken',
  ID_TOKEN: 'auth.idToken',
  EXPIRES_AT: 'auth.expiresAt',
} as const;

/**
 * Storage adapter interface
 */
interface StorageAdapter {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * MMKV Storage Adapter (React Native)
 */
class MMKVStorageAdapter implements StorageAdapter {
  private storage: MMKV | null = null;

  constructor() {
    try {
      // Dynamically import MMKV only on React Native
      if (Platform.OS !== 'web') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { MMKV } = require('react-native-mmkv');
        this.storage = new MMKV({
          id: 'auth-storage',
          encryptionKey: 'wastelinq-auth-encryption-key', // In production, use a secure key from device keychain
        });
      }
    } catch (error) {
      console.error('Failed to initialize MMKV storage:', error);
      // Fallback to memory storage or throw error
      throw new Error('MMKV storage initialization failed');
    }
  }

  setItem(key: string, value: string): void {
    if (!this.storage) {
      throw new Error('MMKV storage not initialized');
    }
    this.storage.set(key, value);
  }

  getItem(key: string): string | null {
    if (!this.storage) {
      throw new Error('MMKV storage not initialized');
    }
    return this.storage.getString(key) ?? null;
  }

  removeItem(key: string): void {
    if (!this.storage) {
      throw new Error('MMKV storage not initialized');
    }
    this.storage.delete(key);
  }

  clear(): void {
    if (!this.storage) {
      throw new Error('MMKV storage not initialized');
    }
    // Clear only auth-related keys
    Object.values(STORAGE_KEYS).forEach((key) => {
      this.storage!.delete(key);
    });
  }
}

/**
 * LocalStorage Adapter (Web)
 */
class LocalStorageAdapter implements StorageAdapter {
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('LocalStorage setItem failed:', error);
      throw error;
    }
  }

  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('LocalStorage getItem failed:', error);
      return null;
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('LocalStorage removeItem failed:', error);
    }
  }

  clear(): void {
    try {
      // Clear only auth-related keys
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('LocalStorage clear failed:', error);
    }
  }
}

/**
 * Get platform-specific storage adapter
 */
const getStorageAdapter = (): StorageAdapter => {
  if (Platform.OS === 'web') {
    return new LocalStorageAdapter();
  } else {
    return new MMKVStorageAdapter();
  }
};

// Initialize storage adapter
const storage = getStorageAdapter();

/**
 * Token Storage API
 */
export const tokenStorage = {
  /**
   * Save tokens to secure storage
   */
  saveTokens(tokens: TokenData): void {
    try {
      storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
      storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
      storage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.idToken);
      storage.setItem(STORAGE_KEYS.EXPIRES_AT, tokens.expiresAt.toString());
    } catch (error) {
      console.error('Failed to save tokens:', error);
      throw new Error('Token storage failed');
    }
  },

  /**
   * Retrieve tokens from secure storage
   */
  getTokens(): TokenData | null {
    try {
      const accessToken = storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const idToken = storage.getItem(STORAGE_KEYS.ID_TOKEN);
      const expiresAt = storage.getItem(STORAGE_KEYS.EXPIRES_AT);

      if (!accessToken || !refreshToken || !idToken || !expiresAt) {
        return null;
      }

      return {
        accessToken,
        refreshToken,
        idToken,
        expiresAt: parseInt(expiresAt, 10),
      };
    } catch (error) {
      console.error('Failed to retrieve tokens:', error);
      return null;
    }
  },

  /**
   * Clear all stored tokens
   */
  clearTokens(): void {
    try {
      storage.clear();
    } catch (error) {
      console.error('Failed to clear tokens:', error);
      throw new Error('Token clear failed');
    }
  },

  /**
   * Check if tokens are expired
   */
  areTokensExpired(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return true;
    }
    // Add 5-minute buffer (300000ms)
    const bufferTime = 5 * 60 * 1000;
    return Date.now() >= tokens.expiresAt - bufferTime;
  },

  /**
   * Get time until token expiration (in milliseconds)
   */
  getTimeUntilExpiration(): number | null {
    const tokens = this.getTokens();
    if (!tokens) {
      return null;
    }
    const timeRemaining = tokens.expiresAt - Date.now();
    return timeRemaining > 0 ? timeRemaining : 0;
  },
};
